import type { Mention } from '@hermes/db/schema';

/**
 * Inline mention tokens — the ROBUST, offset-free representation.
 *
 * A mention lives INSIDE the content string, so it moves with the text and
 * cannot drift when content is rewritten. This replaces the legacy
 * `{offset, length}` sidecar, which desynced whenever any code path edited
 * `content` without re-maintaining offsets (e.g. the hermes
 * `rewrite-mastermind-posts` handler swapping a recording URL for a
 * `[mastermind:<uuid>]` token) — producing chips that rendered in the wrong
 * place and swallowed surrounding text.
 *
 * Format (mirrors the existing `[mastermind:<uuid>]` token convention):
 *   [mention:user:<uuid>:<display>]
 *   [mention:internal:<uuid>:<display>]
 *   [mention:channel:<uuid>:<display>]
 *   [mention:everyone:<display>]
 *
 * The display name is embedded so a chip renders without an entity lookup and
 * survives entity deletion. On the backend, a DB trigger regenerates
 * `community_posts.mentions` / `messages.mentions` from these tokens on every
 * write, so the mention-notification path stays correct with no offset
 * bookkeeping anywhere.
 */
export const MENTION_TOKEN = /\[mention:(user|internal|channel|everyone):([^\]]+)\]/g;

export type MentionKind = 'user' | 'internal' | 'channel' | 'everyone';

export type MentionSegment =
  | { type: 'text'; value: string }
  | { type: 'mention'; mention: Mention };

/** Fast guard — cheap substring check before running the regex. */
export function hasMentionToken(text: string): boolean {
  return text.includes('[mention:');
}

/**
 * Split content into ordered text / mention segments. Text segments are
 * returned verbatim (callers run their own URL auto-linking over them).
 */
export function parseMentionSegments(text: string): MentionSegment[] {
  const segments: MentionSegment[] = [];
  let last = 0;
  for (const m of text.matchAll(MENTION_TOKEN)) {
    const start = m.index ?? 0;
    if (start > last) {
      segments.push({ type: 'text', value: text.slice(last, start) });
    }
    segments.push({ type: 'mention', mention: tokenToMention(m[1] as MentionKind, m[2]) });
    last = start + m[0].length;
  }
  if (last < text.length) {
    segments.push({ type: 'text', value: text.slice(last) });
  }
  return segments;
}

// offset/length are vestigial in the token model — kept at 0 only to satisfy
// the shared Mention type (renderChip never reads them). They are dropped
// entirely once the editor stops emitting the legacy offset shape.
function tokenToMention(kind: MentionKind, payload: string): Mention {
  if (kind === 'everyone') {
    return { kind: 'everyone', displayName: payload, offset: 0, length: 0 } as Mention;
  }
  const sep = payload.indexOf(':');
  const id = sep >= 0 ? payload.slice(0, sep) : payload;
  const displayName = sep >= 0 ? payload.slice(sep + 1) : payload;
  return { kind, id, displayName, offset: 0, length: 0 } as Mention;
}

/** Serialise a mention into its inline token (used by the editor on save). */
export function mentionToToken(m: Mention): string {
  if (m.kind === 'everyone') return `[mention:everyone:${m.displayName}]`;
  return `[mention:${m.kind}:${(m as { id: string }).id}:${m.displayName}]`;
}

/**
 * Editor → storage. Replace each VALID mention span ("@displayName" at its
 * offset) with its inline token, right-to-left so earlier offsets stay valid.
 * A mention whose span no longer matches is skipped — we never tokenise a
 * drifted offset into a misplaced token.
 */
export function tokenizeContent(
  content: string,
  mentions: Mention[] | null | undefined,
): string {
  if (!mentions || mentions.length === 0) return content;
  const ordered = [...mentions].sort((a, b) => b.offset - a.offset);
  let out = content;
  for (const m of ordered) {
    if (out.slice(m.offset, m.offset + m.length) !== `@${m.displayName}`) continue;
    out = out.slice(0, m.offset) + mentionToToken(m) + out.slice(m.offset + m.length);
  }
  return out;
}

/**
 * Storage → plain text. Replace every inline mention token with its
 * "@displayName" plain-text form, dropping the kind/id payload. Used wherever a
 * post's `content` is surfaced as a plain-text excerpt/preview (e.g. the
 * `shared_post_excerpt` on an auto-share chat message) so the raw
 * `[mention:channel:<uuid>:Name]` token never leaks into the UI.
 *
 * Delegates to `parseMentionSegments` so the id/displayName split matches stored
 * semantics exactly (including `everyone`, whose displayName is the whole
 * payload). Token-free text is returned unchanged.
 */
export function stripMentionTokens(text: string): string {
  if (!hasMentionToken(text)) return text;
  return parseMentionSegments(text)
    .map((seg) => (seg.type === 'text' ? seg.value : `@${seg.mention.displayName}`))
    .join('');
}

/**
 * Storage → editor. Expand inline tokens back to "@displayName" plain text and
 * rebuild offset mentions for the textarea session. Content with no tokens is
 * returned unchanged with no mentions (callers fall back to the stored legacy
 * offset mentions in that case).
 */
export function detokenizeContent(stored: string): {
  content: string;
  mentions: Mention[];
} {
  if (!hasMentionToken(stored)) return { content: stored, mentions: [] };
  let content = '';
  const mentions: Mention[] = [];
  for (const seg of parseMentionSegments(stored)) {
    if (seg.type === 'text') {
      content += seg.value;
      continue;
    }
    const label = `@${seg.mention.displayName}`;
    mentions.push({ ...seg.mention, offset: content.length, length: label.length });
    content += label;
  }
  return { content, mentions };
}
